import type { UnstyledButtonProps } from "@mantine/core";
import { Group, Title, Box, Text, Stack, UnstyledButton, Badge } from "@mantine/core";
import { IconExternalLink } from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import _ from "lodash";

import type { PostGeoname } from "prisma/benchmarking/client";

import { ExplorerRangeBar } from "./ExplorerRangeBar";
import { packSx } from "./mantine-extended";
import { RangeLabel, rangeLabelWidth } from "./RangeLabel";
import { getExplorerIndentedWidth } from "./utils/getExplorerIndent";
import { formatYearsExperience } from "../utils/utils";

dayjs.extend(relativeTime);

type PostGeonameLinkProps = {
  post: PostGeoname & { inactive_at: string | null };
} & UnstyledButtonProps;

export function PostGeonameLink({ post, sx = {}, ...rest }: PostGeonameLinkProps) {
  const isInactive = !_.isNil(post.inactive_at);

  function determineComponent() {
    if (isInactive) {
      return "div";
    } else if (post.url) {
      return "a";
    } else {
      return "button";
    }
  }

  return (
    <UnstyledButton
      component={determineComponent()}
      href={post.url ?? undefined}
      target="_blank"
      mt={4}
      ml={{ base: 0, sm: 20 }}
      py={8}
      pr={4}
      pl={{ base: 0, sm: 8 }}
      sx={[
        {
          // Otherwise displays as inline when rendered as "a"
          display: "block",

          textDecoration: "none",
          border: "1px solid transparent",
          borderRadius: 8,

          // if isInactive dull the post

          "&:hover": {
            backgroundColor: "var(--mantine-color-gray-1)",
            transition: "all 0.05s ease-in-out 0s",
            cursor: isInactive ? "not-allowed" : "pointer",
            border: "1px solid var(--mantine-color-gray-3)",
          },
        },
        ...packSx(sx),
      ]}
      {...rest}
    >
      <Group gap={0} wrap="nowrap" w="100%">
        <Group
          w={{
            // 16 here represents additional padding
            base: `calc(100% - ${rangeLabelWidth.base + 16}px)`,
            sm: "auto",
          }}
          wrap="nowrap"
        >
          <Box mr={8} miw={24}>
            {!isInactive && <IconExternalLink size={16} color="var(--mantine-color-gray-5)" />}
          </Box>
          <Stack gap={4} w="100%">
            <Title
              w={getExplorerIndentedWidth({ indentLevel: 2 })}
              c={isInactive ? "gray.6" : "gray.9"}
              order={6}
              sx={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {post.title}
            </Title>
            <Group>
              <Text c="gray.6" fz={{ base: 11, sm: "xs" }}>
                {post.geonameMatch}
              </Text>
              {post.yearsExperienceLow && " · "}
              {post.yearsExperienceLow && (
                <Text c="gray.6" fz={{ base: 11, sm: "xs" }}>
                  {formatYearsExperience(post.yearsExperienceLow, post.yearsExperienceHigh)}
                </Text>
              )}
              {isInactive && " · "}
              {isInactive && (
                <Text c="gray.6" fz={{ base: 11, sm: "xs" }}>
                  Last Posted: {dayjs(post.inactive_at).format("MMM YYYY")}
                </Text>
              )}
              {isInactive ? (
                <Badge
                  sx={{
                    textTransform: "none",
                    color: "var(--mantine-color-gray-6)",
                    backgroundColor: "var(--mantine-color-gray-2)",
                    fontWeight: 500,
                  }}
                >
                  Inactive
                </Badge>
              ) : (
                <Badge sx={{ textTransform: "none", fontWeight: 500 }} color="brand">
                  Active
                </Badge>
              )}
            </Group>
          </Stack>
        </Group>
        <Box style={{ flexGrow: 1 }}>
          <ExplorerRangeBar lowValue={post.salaryLow} highValue={post.salaryHigh} />
        </Box>
        <RangeLabel rangeLow={post.salaryLow} rangeHigh={post.salaryHigh} />
      </Group>
    </UnstyledButton>
  );
}
